import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import {
  Education,
  ProfessionalExperience,
  References,
  VolunteerExperience,
} from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import Utils from '../../../utils/Utils';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import config from '../../../config';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getUbuntuFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  let color3;
  if (color === '#14ae9f') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  if (color === '#468f9a') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  if (color === '#9dd9b8') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  if (color === '#38b8ba') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  if (color === '#006666') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  if (color === '#49308b') {
    color1 = '#333c4e';
    color2 = '#5F5F5F';
    color3 = '#ecedef';
  }
  return { color1, color2, color3 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      // flex: 1,
      // flexDirection: 'row',
      fontFamily: 'Ubuntu',
      paddingBottom: 24,
    },
    contentWrapper: {
      flex: 1,
      flexDirection: 'column',
      width: '100%',
    },
    iconText: {
      fontSize: 9,
      fontWeight: 'light',
      flexWrap: 'wrap',
      fontStyle: 'italic',
    },
    refContactText: {
      fontSize: 9,
      fontWeight: 'light',
      flexWrap: 'wrap',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    header: {
      flexDirection: 'column',
      height: 'auto',
      marginHorizontal: 6,
      paddingTop: 24,
      width: 'auto',
      backgroundColor: '#ffffff',
    },
    headerName: {
      color: props?.color ? colors(props.color).color1 : '#333c4e',
      fontSize: 24,
      fontWeight: 'normal',
      marginLeft: 153,
    },
    headerStatement: {
      color: props?.color ? props.color : '#0f0f0f',
      fontSize: 12,
      fontWeight: 'normal',
      marginLeft: 153,
      marginBottom: 12,
    },
    summaryWrapper: {
      backgroundColor: props?.color ? colors(props.color).color1 : '#333c4e',
      position: 'relative',
      borderTopLeftRadius: 5,
      borderTopRightRadius: 5,
    },
    summary: {
      color: '#ffffff',
      paddingVertical: 15,
      marginLeft: 153,
      width: 410,
    },
    contacts: {
      paddingVertical: 6,
      paddingHorizontal: 24,
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'center',
      flexWrap: 'wrap',
      fontSize: 8,
      backgroundColor: props?.color ? props.color : '#43959b',
      borderBottomLeftRadius: 5,
      borderBottomRightRadius: 5,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 6,
    },
    durationCountryRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    headerContactText: {
      fontSize: 9,
      marginLeft: 3,
      fontWeight: 'medium',
      textDecoration: 'none',
      color: '#ffffff',
    },
    titleBackground: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      marginBottom: 8,
      marginTop: 15,
    },
    titleImageWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: props?.color ? colors(props.color).color1 : '#333c4e',
      width: 24,
      height: 24,
      borderRadius: '50%',
      marginRight: 5,
    },
    titleText: {
      fontSize: 14,
      fontWeight: 'semibold',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    designation: {
      fontSize: 12,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    company: {
      fontSize: 12,
      marginTop: 1,
      fontWeight: 'light',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    lang: {
      fontSize: 11,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    fluency: {
      fontSize: 10,
      marginTop: 1,
      fontWeight: 'light',
      color: props?.color ? props.color : '#333c4e',
    },
    durationCountryWrapper: {
      flexDirection: 'row',
      width: '100%',
      justifyContent: 'space-between',
      marginTop: 4,
      marginBottom: 4,
      gap: 15,
    },
    country: {
      fontSize: 9,
      fontWeight: 'light',
      color: props?.color ? props.color : '#43959b',
      marginLeft: 10,
      fontStyle: 'italic',
    },
    duration: {
      fontSize: 9,
      fontWeight: 'light',
      color: props?.color ? props.color : '#43959b',
      fontStyle: 'italic',
    },
    description: {
      fontSize: 8,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#6F6F6F',
    },
    dottedLine: {
      marginTop: 10,
      borderBottomWidth: 1,
      borderBottomColor: props?.color ? colors(props.color).color2 : '#E0E0E0',
      borderStyle: 'dashed',
    },
    resultsWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
    },
    results: {
      fontSize: 10,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    languages: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 6,
      marginBottom: 5,
    },
    skillWrapper: {
      flexDirection: 'column',
      flexWrap: 'wrap',
      rowGap: 6,
      marginTop: 2,
    },
    skills: {
      fontSize: 10,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#333c4e',
    },
    refName: {
      fontSize: 12,
      fontWeight: 'medium',
      color: '#333c4e',
    },
    refDesignation: {
      fontSize: 10,
      marginTop: 2,
      fontWeight: 'normal',
      color: '#333c4e',
    },
    refOrganization: {
      fontSize: 9,
      fontWeight: 'light',
      marginTop: 2,
      color: '#333c4e',
    },
    refContact: {
      flexDirection: 'column',
      width: '100%',
      alignItems: 'flex-start',
      marginTop: 3,
      gap: 3,
    },
    innerPictureBorder: {
      backgroundColor: props?.color ? props.color : '#ffffff',
      width: 104,
      height: 104,
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    outerPictureBorder: {
      backgroundColor: props?.color ? colors(props.color).color1 : '#333c4e',
      borderRadius: '50%',
      width: 108,
      height: 108,
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      top: -54,
      left: 24,
    },
    content: {
      flexDirection: 'row',
      padding: 6,
      height: 'auto',
    },
    leftColumn: {
      width: 388,
      flexDirection: 'column',
      paddingHorizontal: 15,
      marginRight: 6,
    },
    rightColumn: {
      flexDirection: 'column',
      width: 194,
      height: '100%',
      maxHeight: '100%',
      backgroundColor: 'transparent',
      paddingHorizontal: 18,
      borderRadius: 5,
      flex: 1,
    },
    sideBar: {
      position: 'absolute',
      top: 5,
      right: 6,
      bottom: 5,
      width: 194,
      borderRadius: 5,
      backgroundColor: props?.color ? colors(props.color).color3 : '#f0f0f0',
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <Text style={styles.designation}>{value.designation.value}</Text>
      <Text style={styles.company}>{value.company.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={selectedColor}
          isIcon={false}
        />
        <IconTextView
          text={value.country || ''}
          img={`${config.frontendUrl}/locationLine.png`}
          selectedColor={selectedColor}
          isIcon={false}
        />
      </View>
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {!isLast && <Text />}
    </View>
  );
};
const VolCard: FC<{ value: VolunteerExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <Text style={styles.designation}>{value.organization.value}</Text>
      <Text style={styles.company}>{value.role.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={colors(selectedColor ?? '#14ae9f').color2}
          isIcon={false}
        />
      </View>
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {!isLast && <Text />}
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [`${fromStr} - ${toStr}`, fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <View>
        {type === 'Certification' ? (
          <>
            <Text style={styles.designation}>{`${results[0]?.subject?.value ?? type}`}</Text>
          </>
        ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
          <>
            <Text style={styles.designation}>{type}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result}),` : ','
                }`}</Text>
              ))}
            </View>
          </>
        ) : type === 'Other' ? (
          <>
            <Text style={styles.designation}>{title ?? ''}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              ))}
            </View>
          </>
        ) : (
          results.map((r, i) => (
            <Text key={i} style={styles.designation}>{`${r.subject?.value}${
              r.result ? `, ${r.result}` : ''
            }`}</Text>
          ))
        )}
      </View>
      {institute && <Text style={styles.company}>{institute.value}</Text>}
      {type !== 'Certification' && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[0]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
        </View>
      )}
      {type === 'Certification' && value?.period && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[1]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={colors(selectedColor ?? '#14ae9f').color2}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={colors(selectedColor ?? '#14ae9f').color2}
            isIcon={false}
          />
        </View>
      )}
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {achievements && (
        <View style={styles.description}>
          <PdfHtml level={2} fontFamily="Ubuntu">
            {achievements.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
      {!isLast && <Text />}
    </View>
  );
};
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const activity = value ?? [];
  const data = activity.map((item, index) => {
    const isLastActivity = index === activity.length - 1;
    return (
      <View wrap={false} key={index}>
        {index === 0 && <Text style={styles.designation}>Activities & Societies</Text>}
        {item.institute && item.description && (
          <Text style={[styles.company, { marginTop: 10 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <View wrap={false} style={{ marginTop: 6 }} key={v}>
                <DescriptionText text={v} fontSize="9" lineHeight={1.4} />
              </View>
            );
          }
        })}
        {!isLastActivity && <Text />}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
  width?: string;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth, width }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={[styles.row, { width: width }]}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 12,
          height: iconHeight ?? 12,
          marginRight: 1,
          color: selectedColor,
          padding: 1,
        }}
      />
      <View style={{ paddingHorizontal: 2, paddingVertical: 2 }}>
        {isUrl ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.headerContactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
  isLast?: boolean;
}> = ({ value, selectedColor, isLast }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <Text style={styles.refName}>
        {value.firstName} {value.lastName}
      </Text>
      <Text style={styles.refDesignation}>{value.designation}</Text>
      <Text style={styles.refOrganization}>{value.organization}</Text>
      <View style={styles.refContact}>
        <Text style={styles.refContactText}>{value.email}</Text>
        <Text style={styles.refContactText}>{value.mobile}</Text>
      </View>
      {!isLast && <Text />}
    </View>
  );
};

const TitleText: FC<{
  title: string;
  selectedColor?: string;
  img?: string;
}> = ({ title, selectedColor, img }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.titleBackground}>
      <View style={styles.titleImageWrapper}>
        {img && (
          <Image
            src={img}
            style={{
              width: 14,
              height: 14,
              color: colors(selectedColor ?? '#14ae9f').color2,
            }}
          />
        )}
      </View>

      <Text style={styles.titleText}>{title.toUpperCase()}</Text>
    </View>
  );
};

const DescriptionText: FC<{
  text: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: string;
  lineHeight?: number;
}> = ({ text, color, fontSize, fontFamily, fontWeight, lineHeight }) => {
  return (
    <PdfHtml
      level={2}
      fontSize={fontSize}
      textColor={color}
      fontFamily={fontFamily ? fontFamily : 'Ubuntu'}
      fontWeight={fontWeight}
      lineHeight={lineHeight}>
      {text.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
    </PdfHtml>
  );
};
const IconTextView: FC<{
  text: string;
  img: string;
  isIcon: boolean;
  selectedColor?: string;
  iconWidth?: number;
  iconHeight?: number;
}> = ({ text, img, selectedColor, iconWidth, iconHeight, isIcon }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.durationCountryRow}>
      {isIcon && (
        <Image
          src={img}
          style={{
            width: iconWidth ?? 10,
            height: iconHeight ?? 10,
            marginRight: 2,
            color: colors(selectedColor ?? '#14ae9f').color2,
            padding: 1,
          }}
        />
      )}
      <Text style={[styles.iconText, { color: selectedColor }]}>{text}</Text>
    </View>
  );
};

const Template4 = (props: Props) => {
  const newColor = props.selectedColor ? props.selectedColor : '#468f9a';
  const styles = useMemo(() => pageStyles({ color: newColor }), [newColor]);
  const { profile } = props;

  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View fixed style={styles.sideBar} />

        {/* HEADER SECTION */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerName}>{profile.name}</Text>
            <Text style={styles.headerStatement}>{profile.personalStatementHeader}</Text>
          </View>
          <View style={styles.summaryWrapper}>
            {profile?.picture ? (
              <View style={styles.outerPictureBorder}>
                <View style={styles.innerPictureBorder}>
                  <Image
                    style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
                    src={profile.picture ? profile.picture : config.frontendUrl + '/userImage.jpg'}
                  />
                </View>
              </View>
            ) : (
              <></>
            )}
            {isShowSummery && (
              <View style={styles.summary}>
                {profile.personalStatement && (
                  <DescriptionText
                    color="#ffffff"
                    text={profile.personalStatement}
                    fontSize="9"
                    lineHeight={1.3}
                    fontWeight="semibold"
                  />
                )}
              </View>
            )}
          </View>
          <View style={styles.contacts}>
            {profile?.mobile && (
              <ContactDetailView
                value={profile.mobile}
                img={`${config.frontendUrl}/phone_light.png`}
                selectedColor={colors(newColor).color3}
                width="25%"
              />
            )}
            {profile?.residentialAddress && (
              <ContactDetailView
                value={profile.residentialAddress}
                img={`${config.frontendUrl}/locationPin_light.png`}
                selectedColor={colors(newColor).color3}
                width="25%"
              />
            )}
            {profile?.linkedInUrl && (
              <ContactDetailView
                isUrl={true}
                title={'Linkedin.com'}
                value={profile.linkedInUrl}
                img={`${config.frontendUrl}/linkedin_light.png`}
                selectedColor={colors(newColor).color3}
                width="25%"
              />
            )}
            {profile?.userId && (
              <ContactDetailView
                isUrl={true}
                title={'Careers360.com'}
                value={`${config.frontendUrl}/profile/${profile.userId}`}
                img={`${config.frontendUrl}/careers360_light.png`}
                selectedColor={colors(newColor).color3}
                width="25%"
              />
            )}
            {profile?.email && (
              <ContactDetailView
                title={profile.email}
                type={'email'}
                value={`mailto:${profile.email}`}
                img={`${config.frontendUrl}/email_light.png`}
                selectedColor={colors(newColor).color3}
                width="100%"
              />
            )}
          </View>
        </View>
        <View style={styles.content}>
          <View style={styles.leftColumn}>
            <View
              fixed
              render={({ pageNumber }) => {
                if (pageNumber !== 1) {
                  return <View style={{ height: 24 }} />;
                }
                return <View />;
              }}
            />
            {/* EXPERIENCE SECTION */}
            {profile.experience && profile.experience.length > 0 && (
              <View wrap={false}>
                <TitleText
                  title="Work Experience"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/experience_light.png`}
                />
                {profile.experience.map((exp, index) => (
                  <ExpCard
                    key={index}
                    value={exp}
                    selectedColor={newColor}
                    isLast={profile.experience ? index === profile.experience.length - 1 : false}
                  />
                ))}
              </View>
            )}
            {/* ACTIVITIES & SOCIETIES SECTION */}
            {isShowActivity && (
              <View>
                <TitleText
                  title="Achievements"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/achieve_light.png`}
                />
                <ActivitySocityView value={profile.activities} selectedColor={newColor} />
              </View>
            )}
            {/* VOLUNTEER SECTION */}
            {profile?.volunteering && profile.volunteering.length > 0 && (
              <View>
                <TitleText
                  title="Volunteering"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/volunteer_light.png`}
                />
                {profile.volunteering.map((vol, index) => (
                  <VolCard
                    key={index}
                    value={vol}
                    selectedColor={newColor}
                    isLast={
                      profile.volunteering ? index === profile.volunteering.length - 1 : false
                    }
                  />
                ))}
              </View>
            )}
          </View>
          <View style={styles.rightColumn}>
            {/* SKILLS SECTION */}
            <View
              fixed
              render={({ pageNumber }) => {
                if (pageNumber !== 1) {
                  return <View style={{ paddingTop: 24 }} />;
                }
                return <View />;
              }}
            />
            {profile?.skills && profile.skills.length > 0 && (
              <View wrap={false}>
                <TitleText
                  title="Skills"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/skill_light.png`}
                />
                <View style={styles.skillWrapper}>
                  {profile.skills.map((skill, index) => (
                    <Text key={index} style={styles.skills}>
                      {skill.value}
                    </Text>
                  ))}
                </View>
              </View>
            )}
            {/* EDUCATION SECTION */}
            {profile.education && profile.education.length > 0 && (
              <View>
                <TitleText
                  title="Education"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/education_light.png`}
                />
                {profile.education.map((edu, index) => (
                  <EduCard
                    key={index}
                    value={edu}
                    selectedColor={newColor}
                    isLast={profile.education ? index === profile.education.length - 1 : false}
                  />
                ))}
              </View>
            )}
            {/* LANGUAGES SECTION */}
            {profile?.languages && profile.languages.length > 0 && (
              <View wrap={false}>
                <View
                  fixed
                  render={({ pageNumber }) => {
                    if (pageNumber !== 1) {
                      return <View style={{ height: 18 }} />;
                    }
                    return <View />;
                  }}
                />
                <TitleText
                  title="Languages"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/language_light.png`}
                />
                <View style={styles.languages}>
                  {profile.languages.map((lang, index) => (
                    <View key={index} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Text style={styles.lang}>{lang.language}</Text>
                      <Text style={styles.fluency}>{lang.fluency}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
            {/* REFERENCES SECTION */}
            {profile?.reference && profile.reference.length > 0 && (
              <View wrap={false}>
                <TitleText
                  title="References"
                  selectedColor={newColor}
                  img={`${config.frontendUrl}/reference_light.png`}
                />
                {profile.reference.map((ref, index) => (
                  <ReferenceView
                    key={index}
                    value={ref}
                    selectedColor={colors(newColor).color1}
                    isLast={profile.reference ? index === profile.reference.length - 1 : false}
                  />
                ))}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default Template4;
